/* ══════════════════════════════════════════════════════════════
   ROCHOODIES — theme interactions
   Lenis (smooth scroll) + GSAP ScrollTrigger (scroll-driven motion)
   + real Shopify AJAX cart / demo preview cart for the homepage grid
   ══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const $ = (s, c) => (c || document).querySelector(s);
  const $$ = (s, c) => Array.from((c || document).querySelectorAll(s));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ───────────────────────── 1. IMAGE FALLBACK ───────────────────────── */
  function guardImage(img) {
    if (img.dataset.guarded) return;
    img.dataset.guarded = '1';
    const fail = () => {
      const media = img.closest('.card__media');
      if (media) { media.classList.add('fallback'); return; }
      const p = img.parentElement;
      if (!p) return;
      p.classList.add('has-fail');
      p.setAttribute('data-fail', img.alt || 'Image unavailable');
    };
    img.addEventListener('error', fail, { once: true });
    /* No synchronous `if (img.complete && naturalWidth === 0) fail()`
       check here on purpose — for a server-rendered <img> (not one
       inserted by JS), .complete can read true with naturalWidth 0
       for a moment while the image is still legitimately mid-fetch,
       not actually broken. On a cold cache that raced this check
       constantly, wrongly slapping the "fallback" state on perfectly
       good images that finished loading a moment later with nothing
       to ever clear it. The 'error' listener above is the reliable,
       race-free signal — it's the only thing that should ever call
       fail(). */
  }
  $$('img').forEach(guardImage);

  /* ───────────────────────── 2. CART DRAWER (shared UI, two data sources) ─────────────────────────
     Shares #scrim with the search overlay below — opening either one
     force-closes the other (openSearch/openSizing are hoisted function
     declarations, so they're safely callable here even though they're
     defined further down this same scope). */
  const drawer = $('#drawer'), scrim = $('#scrim'), hdr = $('#hdr');

  function openCart(on) {
    if (!drawer) return;
    if (on) { openSearch(false); openSizing(false); }
    drawer.classList.toggle('on', on);
    scrim.classList.toggle('on', on);
    if (hdr) hdr.classList.toggle('is-muted', on);
    if (window.lenisInstance) on ? window.lenisInstance.stop() : window.lenisInstance.start();
  }
  const cartBtn = $('#cartBtn');
  if (cartBtn) cartBtn.addEventListener('click', () => openCart(true));
  const drawerClose = $('#drawerClose');
  if (drawerClose) drawerClose.addEventListener('click', () => openCart(false));
  if (scrim) scrim.addEventListener('click', () => { openCart(false); openSearch(false); });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') { openCart(false); openSearch(false); openSizing(false); }
  });

  function bumpCartButton() {
    if (!cartBtn) return;
    cartBtn.classList.remove('pop'); void cartBtn.offsetWidth; cartBtn.classList.add('pop');
  }

  /* ───────────────────────── 2b. SEARCH OVERLAY ─────────────────────────
     Opens in place instead of navigating to /search — the form still
     points there and submits normally on Enter (no-JS fallback, full
     result set), but typing fetches Shopify's predictive search JSON
     endpoint directly and renders a short product list live. */
  const searchOv = $('#searchOv'), searchInput = $('#searchInput'), searchResults = $('#searchResults');

  function openSearch(on) {
    if (!searchOv) return;
    if (on) { openCart(false); openSizing(false); }
    searchOv.classList.toggle('on', on);
    searchOv.setAttribute('aria-hidden', String(!on));
    if (scrim) scrim.classList.toggle('on', on);
    if (hdr) hdr.classList.toggle('is-muted', on);
    if (window.lenisInstance) on ? window.lenisInstance.stop() : window.lenisInstance.start();
    if (on && searchInput) setTimeout(() => searchInput.focus(), 60);
  }
  const searchBtn = $('#searchBtn');
  if (searchBtn) searchBtn.addEventListener('click', () => openSearch(true));
  $$('[data-search-close]').forEach(el => el.addEventListener('click', () => openSearch(false)));

  /* ───────────────────────── 2c. SIZING OVERLAY ─────────────────────────
     Full-screen, not a shared-scrim panel like the two above (it
     supplies its own opaque background instead of a dimmed
     see-through one) — but still fits the same open/close/mutual-
     exclusion shape, so it hooks into the same handful of call sites
     above rather than inventing a separate system.

     Content isn't authored here: on first open, the real /pages/sizing
     document is fetched and its .sizing__wrap is lifted out of the
     response and dropped into #sizingOvBody, then cached in `sizingHtml`
     so reopening doesn't refetch. That keeps the Sizing page (see
     sections/sizing-guide.liquid) the single source of truth — edit it
     once, both the standalone page and this overlay reflect it. */
  const sizingOv = $('#sizingOv'), sizingOvBody = $('#sizingOvBody');
  let sizingHtml = null, sizingLoading = false;

  async function loadSizingContent() {
    if (sizingHtml !== null || sizingLoading || !sizingOvBody) return;
    const url = sizingOv.dataset.sizingUrl;
    if (!url) { sizingOvBody.innerHTML = '<p class="sizing-ov__status">Sizing info isn’t available right now.</p>'; return; }
    sizingLoading = true;
    sizingOvBody.innerHTML = '<p class="sizing-ov__status">Loading…</p>';
    try {
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrap = doc.querySelector('.sizing__wrap');
      sizingHtml = wrap ? wrap.outerHTML : '<p class="sizing-ov__status">Sizing info isn’t available right now.</p>';
    } catch (err) {
      sizingHtml = null; // allow a retry on next open instead of caching a failure
      sizingOvBody.innerHTML = '<p class="sizing-ov__status">Couldn’t load sizing info — <a href="' + url + '">view the sizing page</a> instead.</p>';
      sizingLoading = false;
      return;
    }
    sizingLoading = false;
    sizingOvBody.innerHTML = sizingHtml;
  }

  function openSizing(on) {
    if (!sizingOv) return;
    if (on) { openCart(false); openSearch(false); loadSizingContent(); }
    sizingOv.classList.toggle('on', on);
    sizingOv.setAttribute('aria-hidden', String(!on));
    if (hdr) hdr.classList.toggle('is-muted', on);
    if (window.lenisInstance) on ? window.lenisInstance.stop() : window.lenisInstance.start();
  }
  $$('[data-sizing-open]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); openSizing(true); }));
  const sizingOvClose = $('#sizingOvClose');
  if (sizingOvClose) sizingOvClose.addEventListener('click', () => openSizing(false));
  /* .sizing-ov__scroll (not .sizing-ov itself) is the click target
     here — it's the full-size child that actually receives clicks
     anywhere in the overlay; .sizing-ov__body is only as tall/wide as
     its content, so a click that lands on the empty space around it
     (but still inside .sizing-ov__scroll) is the "clicked outside the
     content" case this is meant to catch. */
  const sizingOvScroll = $('.sizing-ov__scroll');
  if (sizingOvScroll) sizingOvScroll.addEventListener('click', e => { if (e.target === sizingOvScroll) openSizing(false); });

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function cdnResize(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  if (searchInput && searchResults) {
    let debounceTimer = null, activeController = null;

    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const term = searchInput.value.trim();
      if (term.length < 2) {
        if (activeController) activeController.abort();
        searchResults.innerHTML = '';
        return;
      }
      debounceTimer = setTimeout(() => runSearch(term), 220);
    });

    async function runSearch(term) {
      if (activeController) activeController.abort();
      activeController = new AbortController();
      searchResults.classList.add('is-loading');
      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
        const res = await fetch(url, { signal: activeController.signal });
        const data = await res.json();
        /* the search box can keep changing while this request is in
           flight — bail if it no longer matches what's shown, so a
           slow early response can't clobber a faster later one */
        if (searchInput.value.trim() !== term) return;
        renderResults(data, term);
      } catch (err) {
        if (err.name !== 'AbortError') {
          searchResults.innerHTML = '<p class="search-ov__empty">Something went wrong — try again.</p>';
        }
      } finally {
        searchResults.classList.remove('is-loading');
      }
    }

    function renderResults(data, term) {
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      if (!products.length) {
        searchResults.innerHTML = '<p class="search-ov__empty">No results for &ldquo;' + escapeHtml(term) + '&rdquo;</p>';
        return;
      }
      const rows = products.map(p =>
        '<a class="search-ov__row" href="' + p.url + '">' +
          '<img src="' + cdnResize(p.image, 88) + '" alt="" loading="lazy">' +
          '<span class="search-ov__row-info">' +
            '<span class="search-ov__row-title">' + escapeHtml(p.title) + '</span>' +
            /* the predictive-search endpoint returns price as a bare
               decimal string ("48.99"), not formatted money — reuse
               the same moneyFmt() the cart already uses so it shows
               with the shop's real currency symbol/locale */
            '<span class="search-ov__row-price">' + moneyFmt(Math.round(parseFloat(p.price) * 100)) + '</span>' +
          '</span>' +
        '</a>'
      ).join('');
      const seeAll = '<a class="search-ov__all" href="/search?q=' + encodeURIComponent(term) + '&type=product">' +
        'See all results for &ldquo;' + escapeHtml(term) + '&rdquo; &rarr;</a>';
      searchResults.innerHTML = rows + seeAll;
    }

    /* landing directly on /search (a bookmark, a search engine
       result, the overlay's own "See all results" link, pressing
       Enter) — pop the overlay open on top of that plain fallback
       page, pre-filled and already searching, so it feels like the
       same in-page experience rather than a separate page you ever
       actually look at. request.page_type renders straight onto
       <body class="..."> in the layout, so "search" here is reliable
       without needing a data attribute added just for this. */
    if (document.body.classList.contains('search') && searchInput.value.trim().length >= 2) {
      openSearch(true);
      runSearch(searchInput.value.trim());
    }
  }

  function moneyFmt(cents) {
    try {
      return new Intl.NumberFormat(window.themeLocale || 'en-US', {
        style: 'currency',
        currency: window.themeCurrency || 'USD',
        currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function flyToCart(srcImg) {
    if (reduced || !window.gsap || !cartBtn) return;
    const a = srcImg.getBoundingClientRect();
    const b = cartBtn.getBoundingClientRect();
    const fly = srcImg.cloneNode();
    fly.className = 'fly';
    Object.assign(fly.style, { left: a.left + 'px', top: a.top + 'px', width: a.width + 'px', height: a.height + 'px' });
    document.body.appendChild(fly);
    gsap.to(fly, {
      duration: .85, ease: 'power3.inOut', opacity: .15,
      x: b.left + b.width / 2 - a.left - a.width / 2,
      y: b.top + b.height / 2 - a.top - a.height / 2,
      scale: .06, rotate: 14,
      onComplete: () => fly.remove()
    });
  }

  /* ---- Real Shopify AJAX cart ---- */
  const RealCart = {
    async add(variantId, qty) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty || 1 })
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || 'Could not add to bag'); }
      await this.refresh();
    },
    async step(key, d) {
      const li = $('.li[data-line-key="' + CSS.escape(key) + '"]', $('#drawerBody'));
      const current = li ? parseInt($('[data-qty-value]', li).textContent, 10) : 0;
      await this.change(key, Math.max(0, current + d));
    },
    async remove(key) { await this.change(key, 0); },
    async change(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const data = await res.json();
      this.render(data);
    },
    async refresh() {
      const res = await fetch('/cart.js');
      const data = await res.json();
      this.render(data);
      bumpCartButton();
    },
    render(cart) {
      $('#cartCount').textContent = cart.item_count;
      $('#drawerCount').textContent = '(' + cart.item_count + ')';
      $('#sub').textContent = moneyFmt(cart.total_price);
      $('#tot').textContent = moneyFmt(cart.total_price);
      $('#ship').textContent = cart.item_count > 0 ? 'Calculated at checkout' : '—';

      const body = $('#drawerBody');
      if (!cart.items.length) {
        body.innerHTML =
          '<div class="drawer__empty"><span class="mono">Your bag is empty</span>' +
          '<span style="font-size:13px;max-width:26ch;margin-inline:auto">Browse the collection and add something to your bag.</span></div>';
        return;
      }
      body.innerHTML = cart.items.map(i =>
        '<div class="li" data-line-key="' + i.key + '">' +
          '<img src="' + i.image + '" alt="">' +
          '<div><div class="nm">' + i.product_title + '</div>' +
          '<div class="op">' + (i.variant_title || '') + '</div>' +
          '<div class="qty"><button type="button" data-qty-step="-1" aria-label="Decrease quantity">&minus;</button>' +
          '<span data-qty-value>' + i.quantity + '</span>' +
          '<button type="button" data-qty-step="1" aria-label="Increase quantity">+</button></div></div>' +
          '<div><div class="pr" data-line-price>' + moneyFmt(i.final_line_price) + '</div>' +
          '<a class="rm" href="#" data-remove-line>Remove</a></div>' +
        '</div>').join('');
    }
  };

  /* ---- Demo preview cart (used only when the grid has no real products yet) ---- */
  const DemoCart = {
    items: [],
    add(id, name, price, colorName, colorImg, size) {
      const key = id + '|' + colorName + '|' + size;
      const hit = this.items.find(i => i.key === key);
      if (hit) hit.qty++;
      else this.items.push({ key, name, price, color: colorName, img: colorImg, size, qty: 1 });
      this.render(); bumpCartButton();
    },
    remove(key) { this.items = this.items.filter(i => i.key !== key); this.render(); },
    step(key, d) {
      const hit = this.items.find(i => i.key === key);
      if (!hit) return;
      hit.qty += d;
      if (hit.qty < 1) return this.remove(key);
      this.render();
    },
    count() { return this.items.reduce((n, i) => n + i.qty, 0); },
    subtotal() { return this.items.reduce((n, i) => n + i.qty * i.price, 0); },
    render() {
      const n = this.count(), sub = this.subtotal();
      $('#cartCount').textContent = n;
      $('#drawerCount').textContent = '(' + n + ')';
      const ship = n === 0 ? 0 : (sub >= 200 ? 0 : 18);
      $('#sub').textContent = '$' + sub;
      $('#ship').textContent = n === 0 ? '—' : (ship === 0 ? 'Free' : '$' + ship);
      $('#tot').textContent = '$' + (sub + ship);

      const body = $('#drawerBody');
      if (!this.items.length) {
        body.innerHTML =
          '<div class="drawer__empty"><span class="mono">Your bag is empty</span>' +
          '<span style="font-size:13px;max-width:26ch;margin-inline:auto">Preview mode — add real products to enable checkout.</span></div>';
        return;
      }
      body.innerHTML = this.items.map(i =>
        '<div class="li" data-line-key="' + i.key + '">' +
          '<img src="' + i.img + '" alt="">' +
          '<div><div class="nm">' + i.name + '</div>' +
          '<div class="op">' + i.color + ' / ' + i.size + '</div>' +
          '<div class="qty"><button type="button" data-qty-step="-1" aria-label="Decrease quantity">&minus;</button>' +
          '<span data-qty-value>' + i.qty + '</span>' +
          '<button type="button" data-qty-step="1" aria-label="Increase quantity">+</button></div></div>' +
          '<div><div class="pr" data-line-price>$' + (i.price * i.qty) + '</div>' +
          '<a class="rm" href="#" data-remove-line>Remove</a></div>' +
        '</div>').join('');
    }
  };

  const grid = $('#grid');
  const isDemo = !!(grid && grid.hasAttribute('data-demo-grid'));
  const ActiveCart = isDemo ? DemoCart : RealCart;
  if (!isDemo) RealCart.refresh().catch(() => {});
  else DemoCart.render();

  $('#drawerBody').addEventListener('click', e => {
    const q = e.target.closest('[data-qty-step]');
    if (q) {
      const key = q.closest('.li').dataset.lineKey;
      ActiveCart.step(key, +q.dataset.qtyStep);
      return;
    }
    const r = e.target.closest('[data-remove-line]');
    if (r) { e.preventDefault(); ActiveCart.remove(r.closest('.li').dataset.lineKey); }
  });

  /* ───────────────────────── 3. REAL PRODUCT CARDS ─────────────────────────
     Cards are plain links straight to the product page now — no color/
     size picking or add-to-cart from the grid, so there's nothing here
     for JS to wire up. The colour swatches shown on the card are
     display-only (see product-card.liquid); the hover image crossfade
     is handled entirely by CSS (base.css, .card:hover .is-alt). */

  /* ───────────────────────── 4. DEMO PRODUCT CARDS ───────────────────────── */
  $$('[data-demo-card]').forEach(card => {
    const colorsEl = $('[data-demo-colors]', card);
    let colors = { names: [], images: [] };
    if (colorsEl) { try { colors = JSON.parse(colorsEl.textContent); } catch (e) {} }

    const state = { color: 0, size: null };
    const base = $('.is-base', card);
    const alt = $('.is-alt', card);
    const cName = $('.c-name', card);
    const sName = $('.s-name', card);
    const sizes = $('.sizes', card);
    const addBtn = $('.add-btn', card);
    const name = $('.card__name a', card).textContent.trim();
    const price = parseInt(card.querySelector('.card__price').textContent.replace(/\D/g, ''), 10);
    const id = card.dataset.id;

    $$('.sw', card).forEach(sw => {
      sw.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        const i = +sw.dataset.i;
        state.color = i;
        $$('.sw', card).forEach(o => o.setAttribute('aria-pressed', o === sw));
        if (cName) cName.textContent = colors.names[i];
        if (base) base.src = colors.images[i];
        if (alt) alt.src = colors.images[(i + 1) % colors.images.length];
        if (window.gsap && base) gsap.fromTo(base, { opacity: .25, scale: 1.05 }, { opacity: 1, scale: 1, duration: .55, ease: 'power3.out' });
      });
    });

    $$('.sz', card).forEach(sz => {
      sz.addEventListener('click', e => {
        e.preventDefault(); e.stopPropagation();
        state.size = sz.dataset.s;
        $$('.sz', card).forEach(o => o.setAttribute('aria-pressed', o === sz));
        if (sName) sName.textContent = state.size;
      });
    });

    if (addBtn) addBtn.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      if (!state.size) {
        sizes.classList.remove('shake'); void sizes.offsetWidth; sizes.classList.add('shake');
        if (sName) { sName.textContent = 'Pick one'; sName.style.color = 'var(--g-900)'; setTimeout(() => { sName.style.color = ''; }, 1400); }
        return;
      }
      DemoCart.add(id, name, price, colors.names[state.color], colors.images[state.color], state.size);
      flyToCart(base);
      addBtn.textContent = 'Added ✓';
      addBtn.classList.add('done');
      setTimeout(() => { addBtn.textContent = 'Add to bag — $' + price; addBtn.classList.remove('done'); }, 1600);
    });
  });

  /* ───────────────────────── 5. PRODUCT PAGE ───────────────────────── */
  (function () {
    const mapEl = $('#pdpVariantMap');
    if (!mapEl) return;

    let map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return; }

    const colorIdx = map.optionNames.findIndex(n => /^colou?r$/i.test(n));
    const sizeIdx = map.optionNames.findIndex(n => /^size$/i.test(n));

    const swatches = $$('#pdpSwatches .sw');
    const sizeBtns = $$('#pdpSizes .sz');
    const cName = $('#pdpColorName');
    const sName = $('#pdpSizeName');
    const priceEl = $('#pdpPrice');
    const compareEl = $('#pdpCompare');
    const idInput = $('#pdpVariantId');
    const addBtn = $('#pdpAddBtn');
    const addLabel = $('#pdpAddLabel');
    const media = $('#pdpGallery');

    const selections = new Array(map.optionNames.length).fill(null);
    const pressed = $('#pdpSwatches .sw[aria-pressed="true"]');
    if (colorIdx >= 0 && pressed) selections[colorIdx] = pressed.dataset.color;
    const pressedSz = $('#pdpSizes .sz[aria-pressed="true"]');
    if (sizeIdx >= 0 && pressedSz) selections[sizeIdx] = pressedSz.dataset.size;

    function resolveVariant() {
      return map.variants.find(v => map.optionNames.every((name, i) =>
        selections[i] == null || v.options[i] === selections[i]
      ));
    }

    function refreshSizeAvailability() {
      if (sizeIdx < 0 || colorIdx < 0) return;
      sizeBtns.forEach(btn => {
        const ok = map.variants.some(v =>
          v.options[colorIdx] === selections[colorIdx] &&
          v.options[sizeIdx] === btn.dataset.size &&
          v.available
        );
        btn.disabled = !ok;
      });
    }

    /* Every gallery image is tagged server-side with the color it belongs
       to (data-color), inferred from which variant's featured image it is.
       Images with no color tag are generic (detail shots, size charts)
       and stay visible no matter which color is selected. */
    const galleryImgs = media ? $$('.pdp__img', media) : [];
    const hasColorImages = galleryImgs.some(el => el.dataset.color);

    function applyGalleryFilter(color) {
      if (!hasColorImages || !color) return;
      let anyVisible = false;
      galleryImgs.forEach(el => {
        const show = !el.dataset.color || el.dataset.color === color;
        el.classList.toggle('is-color-hidden', !show);
        if (show) anyVisible = true;
      });
      /* safety net: never end up with an empty gallery */
      if (!anyVisible) galleryImgs.forEach(el => el.classList.remove('is-color-hidden'));
    }

    /* ---- gallery carousel: its own scoped, horizontal Lenis instance ---- */
    const gallery = {};
    (function initGallery() {
      if (!media) return;
      const viewport = $('#pdpGalleryViewport');
      const track = $('#pdpGalleryTrack');
      if (!viewport || !track) return;
      const prevBtn = $('[data-gallery-prev]', media);
      const nextBtn = $('[data-gallery-next]', media);

      let glenis = null;
      const LenisCtor2 = window.Lenis && (window.Lenis.default || window.Lenis);
      if (LenisCtor2 && !reduced && galleryImgs.length > 1) {
        glenis = new LenisCtor2({
          wrapper: viewport,
          content: track,
          orientation: 'horizontal',
          gestureOrientation: 'horizontal',
          smoothWheel: true,
          wheelMultiplier: 1,
          /* let the browser's native touch scrolling handle swipes —
             Lenis here is only for the arrow-button scrollTo animation */
          syncTouch: false
        });
        /* drive it off the same ticker the page's main smooth scroll
           uses, rather than Lenis's own autoRaf, for one consistent
           animation loop across the whole theme */
        if (window.gsap) {
          gsap.ticker.add(t => glenis.raf(t * 1000));
        } else {
          const pump = t => { glenis.raf(t); requestAnimationFrame(pump); };
          requestAnimationFrame(pump);
        }
      }

      let index = 0;
      let ignoreScroll = false;

      function visible() { return galleryImgs.filter(el => !el.classList.contains('is-color-hidden')); }

      function goTo(i, animate) {
        const vis = visible();
        if (!vis.length) return;
        index = Math.max(0, Math.min(vis.length - 1, i));
        const target = vis[index];
        ignoreScroll = true;
        if (glenis) {
          glenis.scrollTo(target, { duration: animate === false ? 0 : 0.85, onComplete: () => { ignoreScroll = false; } });
        } else if (viewport) {
          viewport.scrollTo({ left: target.offsetLeft, behavior: animate === false ? 'auto' : 'smooth' });
          ignoreScroll = false;
        }
      }

      if (glenis) {
        glenis.on('scroll', e => {
          if (ignoreScroll) return;
          const vis = visible();
          if (!vis.length) return;
          let nearest = 0, dist = Infinity;
          vis.forEach((el, i) => {
            const d = Math.abs(el.offsetLeft - e.animatedScroll);
            if (d < dist) { dist = d; nearest = i; }
          });
          index = nearest;
        });
      }

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(index - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(index + 1));

      /* mouse click-and-drag scrolling — browsers don't support this
         natively (unlike touch, which already scrolls fine on its own
         now that Lenis isn't intercepting it). Mouse-only: touch/pen
         pointers fall through to native touch scrolling untouched. */
      let dragging = false, dragStartX = 0, dragStartScroll = 0, dragMoved = false;
      viewport.addEventListener('pointerdown', e => {
        if (e.pointerType !== 'mouse') return;
        dragging = true; dragMoved = false;
        dragStartX = e.clientX;
        dragStartScroll = viewport.scrollLeft;
        viewport.classList.add('is-dragging');
        track.classList.add('is-dragging');
        /* can throw in some edge cases (e.g. no active pointer session) —
           the visual dragging state above must not depend on it */
        try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
      });
      viewport.addEventListener('pointermove', e => {
        if (!dragging) return;
        const dx = e.clientX - dragStartX;
        if (Math.abs(dx) > 3) dragMoved = true;
        viewport.scrollLeft = dragStartScroll - dx;
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        viewport.classList.remove('is-dragging');
        track.classList.remove('is-dragging');
        if (dragMoved) {
          /* snap to whichever slide is now closest */
          const vis = visible();
          let nearest = 0, dist = Infinity;
          vis.forEach((el, i) => {
            const d = Math.abs(el.offsetLeft - viewport.scrollLeft);
            if (d < dist) { dist = d; nearest = i; }
          });
          goTo(nearest);
        }
      }
      viewport.addEventListener('pointerup', endDrag);
      viewport.addEventListener('pointercancel', endDrag);
      viewport.addEventListener('pointerleave', endDrag);
      /* swallow the stray click that fires right after a real drag,
         so a drag-release never gets mistaken for a tap/click */
      viewport.addEventListener('click', e => { if (dragMoved) { e.preventDefault(); e.stopPropagation(); } }, true);

      gallery.reset = function (color) {
        /* jump to the newly-selected color's own photo if it has one,
           rather than always snapping back to slide 0 — otherwise,
           whenever slide 0 happens to be a generic (untagged) shot,
           picking a color silently does nothing visible and looks
           broken even though the right variant was actually selected */
        const vis = visible();
        let target = 0;
        if (color) {
          const found = vis.findIndex(el => el.dataset.color === color);
          if (found >= 0) target = found;
        }
        index = target;
        goTo(target, false);
        if (glenis) glenis.resize();
      };
      gallery.firstVisibleImg = function () {
        const vis = visible();
        return vis[0] ? $('img', vis[0]) : $('.pdp__img img', media);
      };
    })();

    function filterGalleryByColor(color) {
      applyGalleryFilter(color);
      if (gallery.reset) gallery.reset(color);
    }

    function firstVisibleImg() {
      if (gallery.firstVisibleImg) return gallery.firstVisibleImg();
      const visibleEl = galleryImgs.find(el => !el.classList.contains('is-color-hidden'));
      return visibleEl ? $('img', visibleEl) : $('.pdp__img img', media);
    }

    function updateUI() {
      const variant = resolveVariant();
      const ready = !!variant;

      if (idInput) idInput.value = variant ? variant.id : '';
      if (priceEl && variant) priceEl.textContent = moneyFmt(variant.price);
      if (compareEl) {
        if (variant && variant.compareAtPrice && variant.compareAtPrice > variant.price) {
          compareEl.textContent = moneyFmt(variant.compareAtPrice);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      const needsSize = sizeIdx >= 0 && selections[sizeIdx] == null;
      if (addBtn) addBtn.disabled = !ready || needsSize || (variant && !variant.available);
      if (addLabel && variant) {
        addLabel.textContent = !variant.available
          ? 'Sold out'
          : needsSize
            ? 'Add to bag'
            : 'Add to bag — ' + moneyFmt(variant.price);
      }

      refreshSizeAvailability();
      return variant;
    }

    swatches.forEach(sw => {
      sw.addEventListener('click', () => {
        if (colorIdx < 0) return;
        selections[colorIdx] = sw.dataset.color;
        swatches.forEach(o => o.setAttribute('aria-pressed', o === sw));
        if (cName) cName.textContent = sw.dataset.color;
        updateUI();
        filterGalleryByColor(sw.dataset.color);
      });
    });

    sizeBtns.forEach(sz => {
      sz.addEventListener('click', () => {
        if (sizeIdx < 0 || sz.disabled) return;
        selections[sizeIdx] = sz.dataset.size;
        sizeBtns.forEach(o => o.setAttribute('aria-pressed', o === sz));
        if (sName) sName.textContent = sz.dataset.size;
        updateUI();
      });
    });

    /* no on-page stepper anymore — always adds 1 (RealCart.add below) */
    const qty = 1;

    if (addBtn) addBtn.addEventListener('click', async e => {
      e.preventDefault();
      const variant = resolveVariant();
      if (!variant || !variant.available) return;
      const original = addLabel ? addLabel.textContent : '';
      addBtn.disabled = true;
      try {
        await RealCart.add(variant.id, qty);
        const heroImg = firstVisibleImg();
        if (heroImg) flyToCart(heroImg);
        if (addLabel) addLabel.textContent = 'Added ✓';
        addBtn.classList.add('done');
      } catch (err) {
        if (addLabel) addLabel.textContent = 'Sold out';
      } finally {
        setTimeout(() => {
          if (addLabel) addLabel.textContent = original;
          addBtn.classList.remove('done');
          addBtn.disabled = false;
        }, 1600);
      }
    });

    updateUI();
    if (colorIdx >= 0 && selections[colorIdx]) {
      applyGalleryFilter(selections[colorIdx]);
      if (gallery.reset) gallery.reset(selections[colorIdx]);
    }
  })();

  /* ───────────────────────── 6. REVIEWS CAROUSEL ─────────────────────────
     Same drag-to-scroll pattern as the product page gallery: its own
     scoped horizontal Lenis instance for the arrow-button animation,
     native touch scrolling for mobile, and a mouse-only pointer-drag
     handler layered on top (browsers don't support click-drag panning
     natively the way they support touch swipe). */
  (function () {
    const gallery = $('#reviewsGallery');
    if (!gallery) return;
    const viewport = $('#reviewsViewport');
    const track = $('#reviewsTrack');
    if (!viewport || !track) return;
    const prevBtn = $('[data-reviews-prev]', gallery);
    const nextBtn = $('[data-reviews-next]', gallery);
    const cards = $$('.review-card', track);
    if (!cards.length) return;

    let rLenis = null;
    const LenisCtor3 = window.Lenis && (window.Lenis.default || window.Lenis);
    if (LenisCtor3 && !reduced && cards.length > 1) {
      rLenis = new LenisCtor3({
        wrapper: viewport,
        content: track,
        orientation: 'horizontal',
        gestureOrientation: 'horizontal',
        smoothWheel: true,
        wheelMultiplier: 1,
        syncTouch: false
      });
      if (window.gsap) {
        gsap.ticker.add(t => rLenis.raf(t * 1000));
      } else {
        const pump = t => { rLenis.raf(t); requestAnimationFrame(pump); };
        requestAnimationFrame(pump);
      }
    }

    let index = 0;
    function goTo(i) {
      index = Math.max(0, Math.min(cards.length - 1, i));
      const target = cards[index];
      if (rLenis) rLenis.scrollTo(target, { duration: .85 });
      else viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(index + 1));

    /* mouse-only drag-to-scroll; touch keeps using native scrolling */
    let dragging = false, dragStartX = 0, dragStartScroll = 0, dragMoved = false;
    viewport.addEventListener('pointerdown', e => {
      if (e.pointerType !== 'mouse') return;
      dragging = true; dragMoved = false;
      dragStartX = e.clientX;
      dragStartScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      track.classList.add('is-dragging');
      try { viewport.setPointerCapture(e.pointerId); } catch (err) {}
    });
    viewport.addEventListener('pointermove', e => {
      if (!dragging) return;
      const dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 3) dragMoved = true;
      viewport.scrollLeft = dragStartScroll - dx;
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
  })();

  /* ───────────────────────── 7. LENIS ───────────────────────── */
  const LenisCtor = window.Lenis && (window.Lenis.default || window.Lenis);
  let lenis = null;

  if (LenisCtor && !reduced) {
    lenis = new LenisCtor({
      duration: 1.15,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6
    });
    window.lenisInstance = lenis;
  }

  /* anchor links routed through Lenis */
  $$('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const id = a.getAttribute('href');
      if (id === '#' || id.length < 2) { e.preventDefault(); return; }
      const t = document.querySelector(id);
      if (!t) return;
      e.preventDefault();
      const off = -(window.innerWidth > 900 ? 60 : 50);
      if (lenis) lenis.scrollTo(t, { offset: off, duration: 1.4 });
      else t.scrollIntoView({ behavior: 'smooth' });
    });
  });

  /* ───────────────────────── 8. GSAP WIRING ───────────────────────── */
  const hasGSAP = !!(window.gsap && window.ScrollTrigger);
  if (!hasGSAP) document.documentElement.classList.remove('js');

  if (hasGSAP) {
    gsap.registerPlugin(ScrollTrigger);
    if (lenis) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(t => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
    }
    ScrollTrigger.defaults({ invalidateOnRefresh: true });
  }

  /* ───────────────────────── 9. HEADER + PROGRESS ─────────────────────────
     The is-stuck threshold (92% of one viewport height) is really
     "roughly past the hero" — fine on the homepage, but on any page
     with no hero at all it just meant a long stretch of nearly-
     unreadable transparent header before the legible solid
     background ever kicked in (see screenshot in chat: header
     overlaying a product grid with no background). Pages without a
     .hero-wrap now get the solid "stuck" look permanently, set once
     up front rather than earning it by scrolling. Pages WITH a hero
     (the homepage) still earn the "stuck"/floating look by scrolling
     past the threshold — the header used to also hide itself
     (translateY(-102%)) on scroll-down past a second threshold, but
     that made the nav randomly slide in and out as you scrolled;
     removed so the header/nav stays visible the whole time, keeping
     only the transparent-over-hero → solid-floating-pill transition. */
  const bar = $('#progressBar');
  const heroWrap = $('.hero-wrap');
  if (hdr && !heroWrap) hdr.classList.add('is-stuck');

  function onScrollFrame(y) {
    const max = document.body.scrollHeight - window.innerHeight;
    if (bar) bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, y / (max || 1))) + ')';
    if (hdr && heroWrap) {
      hdr.classList.toggle('is-stuck', y > window.innerHeight * 0.92);
    }
  }
  if (lenis) lenis.on('scroll', e => onScrollFrame(e.scroll ?? window.scrollY));
  else window.addEventListener('scroll', () => onScrollFrame(window.scrollY), { passive: true });

  /* ───────────────────────── 10. CURSOR ─────────────────────────
     Just a ring that follows the pointer — no hover scale, no fill,
     no label popover. */
  const cur = $('#cursor');
  if (hasGSAP && cur && window.matchMedia('(hover:hover) and (pointer:fine)').matches) {
    const xTo = gsap.quickTo(cur, 'x', { duration: .35, ease: 'power3' });
    const yTo = gsap.quickTo(cur, 'y', { duration: .35, ease: 'power3' });
    window.addEventListener('mousemove', e => { xTo(e.clientX); yTo(e.clientY); });
  }

  /* ───────────────────────── 11. SCROLL SCENES ───────────────────────── */
  function buildScenes() {
    if (!hasGSAP) return;

    if (reduced) {
      gsap.set('.r-up, .card', { opacity: 1, y: 0 });
      gsap.set('.review-card', { opacity: 1, x: 0 });
      return;
    }

    /* ---- hero parallax + curtain reveal ---- */
    const heroWrap = $('.hero-wrap');
    if (heroWrap) {
      gsap.timeline({
        scrollTrigger: { trigger: heroWrap, start: 'top top', end: '+=100%', scrub: .4 }
      })
        .to('#heroImg', { yPercent: 14, scale: 1.16, filter: 'contrast(1.02) saturate(1.04) brightness(0.42)', ease: 'none' }, 0)
        .to('#heroContent', { yPercent: -46, opacity: 0, ease: 'none' }, 0)
        .to('.scroll-cue', { opacity: 0, duration: .25, ease: 'none' }, 0);

      /* 404 page reuses the same #top/.hero-wrap sticky+curtain-reveal
         convention as the homepage hero (so the footer automatically
         pulls up over it — see the shared .shopify-section:has(#top)
         rules in base.css) — this just adds its own parallax content
         to the same scrubbed timeline/trigger. */
      if ($('.hero404')) {
        gsap.timeline({
          scrollTrigger: { trigger: heroWrap, start: 'top top', end: '+=100%', scrub: .4 }
        })
          .to('#hero404Bg', { yPercent: 22, scale: 1.1, ease: 'none' }, 0)
          .to('.hero404__content', { yPercent: -34, opacity: 0, ease: 'none' }, 0);
      }
    }

    /* ---- generic reveals ----
       On mobile, the product page's buy-box content is tall enough
       that individually scroll-triggering each row makes it feel
       like the page loads in two confusing stages (image + title,
       then everything else pops in as you scroll a little). Below
       the 900px breakpoint, reveal all of it together instead. */
    const pdpEl = $('.pdp');
    const pdpMobileReveal = pdpEl && window.innerWidth <= 900;

    $$('.r-up:not(.card)').forEach(el => {
      if (pdpMobileReveal && pdpEl.contains(el)) return;
      gsap.to(el, {
        opacity: 1, y: 0, duration: .95, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        /* clearProps:'transform' alone isn't enough — ".js .r-up"
           sets transform:translateY(30px) in CSS, and that rule stays
           matched forever since the element keeps the .r-up class.
           Clearing just the inline transform let that CSS rule
           reassert itself the instant the tween finished, snapping
           every heading/eyebrow/meta line back down 30px right after
           it revealed (inline opacity:1 stuck around so it stayed
           visible, just shifted — the "text drops onto the divider
           line" bug). Dropping the class first, same as the .card
           batch below, stops the CSS rule from matching again. */
        onComplete: () => { el.classList.remove('r-up'); gsap.set(el, { clearProps: 'transform' }); }
      });
    });

    if (pdpMobileReveal) {
      const pdpRevealEls = $$('.r-up', pdpEl);
      gsap.to(pdpRevealEls, {
        opacity: 1, y: 0, duration: .95, ease: 'power3.out',
        onComplete: () => {
          pdpRevealEls.forEach(el => el.classList.remove('r-up'));
          gsap.set(pdpRevealEls, { clearProps: 'transform' });
        }
      });
    }

    /* ---- card stagger ----
       transform is handed back to CSS afterwards so :hover expansion still works */
    ScrollTrigger.batch('.card', {
      start: 'top 90%', once: true,
      onEnter: batch => {
        batch.forEach(c => { c.style.transition = 'none'; });
        gsap.to(batch, {
          opacity: 1, y: 0, duration: .9, stagger: .075, ease: 'power3.out',
          onComplete: () => batch.forEach(c => {
            c.classList.remove('r-up');
            c.style.transition = '';
            gsap.set(c, { clearProps: 'transform' });
          })
        });
      }
    });

    /* ---- reviews reveal ----
       Cards live in a horizontally-scrolling track, so most of them
       sit off to the right at page load — but they all share the
       same vertical position as the section, so a single trigger on
       the section itself (rather than per-card ScrollTrigger.batch,
       which cares about each element's own bounding box) is what we
       want here: as the user scrolls the section into view, every
       card slides in from the right together, staggered. Replaces
       the old hover pop-out effect entirely.

       No clearProps:'transform' on completion here (unlike the card
       stagger above) — .review-card no longer has any :hover
       transform rule to hand control back to, and the permanent
       ".js .review-card { transform: translateX(70px) }" initial
       state would just reassert itself the instant the inline
       transform was cleared, snapping the cards straight back to
       hidden right after they revealed. Leaving GSAP's inline
       transform in place (translate(0,0)) is the stable end state. */
    const reviewsSection = $('#reviews');
    const reviewCards = reviewsSection ? $$('.review-card', reviewsSection) : [];
    if (reviewsSection && reviewCards.length) {
      gsap.to(reviewCards, {
        x: 0, opacity: 1, duration: 1, stagger: .08, ease: 'power3.out',
        scrollTrigger: { trigger: reviewsSection, start: 'top 75%', once: true }
      });
    }

    /* ---- headline split (per-character line reveal) ----
       [data-split-cta] started as a name for just the newsletter CTA
       headline, but the about page's opening statement reuses the
       exact same treatment — handles as many instances as a page
       actually has, not just the first. */
    $$('[data-split-cta]').forEach(cta => {
      const lines = cta.textContent.split('\n').filter(Boolean);
      cta.innerHTML = '';
      const all = [];
      lines.forEach(ln => {
        const row = document.createElement('span');
        row.style.display = 'block';
        row.style.overflow = 'hidden';
        [...ln].forEach(ch => {
          const s = document.createElement('span');
          s.className = 'ch';
          /* a bare space as an inline-block span's ONLY content is
             both the "start" and "end" of its own inline formatting
             context at once, so normal CSS whitespace collapsing
             trims it away to nothing — words on the same line ran
             together with no gap ("BUILT TO" rendering as "BUILTTO")
             until this used a non-breaking space instead, which is
             never collapsed regardless of position. */
          s.textContent = ch === ' ' ? ' ' : ch;
          row.appendChild(s);
          all.push(s);
        });
        cta.appendChild(row);
      });
      gsap.set(all, { yPercent: 118 });
      gsap.to(all, {
        yPercent: 0, duration: 1, ease: 'power4.out', stagger: .03,
        scrollTrigger: { trigger: cta, start: 'top 80%', once: true }
      });
    });

    /* ---- CTA ring parallax ----
       Two real rings (not one ring + a ::after) so they can drift at
       different rates/directions — the outer ring moves down and
       rotates one way, the inner moves up and rotates the other,
       scrubbed to scroll position across the whole time the section
       is on screen (not just once, unlike the reveals above) for a
       continuous depth effect rather than a one-shot animation. */
    const ctaRing = $('#ctaRing'), ctaRingInner = $('#ctaRingInner');
    if (ctaRing && ctaRingInner) {
      gsap.timeline({
        scrollTrigger: { trigger: '.cta', start: 'top bottom', end: 'bottom top', scrub: .6 }
      })
        .fromTo(ctaRing, { y: -60, rotate: 0 }, { y: 60, rotate: 25, ease: 'none' }, 0)
        .fromTo(ctaRingInner, { y: 40, rotate: 0 }, { y: -40, rotate: -35, ease: 'none' }, 0);
    }

    /* ---- footer wordmark ---- */
    const fw = $('#ftWord');
    if (fw) {
      gsap.fromTo(fw, { yPercent: 105 }, {
        yPercent: 0, ease: 'power3.out', duration: 1.2,
        scrollTrigger: { trigger: '.ft__word', start: 'top 92%', once: true }
      });
    }

    /* ---- ABOUT BACKDROP ----
       The about page's three fixed gradient blobs (about-hero.liquid,
       #aboutBg) drift and the whole layer's hue shifts across the
       entire scroll of the page — trigger is the page itself, not any
       one section, so it's continuous no matter which section is
       currently on screen. Only present on the about template, so
       this is a no-op everywhere else. */
    const aboutBg = $('#aboutBg');
    if (aboutBg) {
      const blobs = $$('.about-bg__blob', aboutBg);
      gsap.timeline({
        scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: .6 }
      })
        .to(blobs[0], { xPercent: 38, yPercent: 58, rotate: 45, ease: 'none' }, 0)
        .to(blobs[1], { xPercent: -32, yPercent: 74, rotate: -40, ease: 'none' }, 0)
        .to(blobs[2], { xPercent: 22, yPercent: -64, rotate: 55, ease: 'none' }, 0)
        .to(aboutBg, { '--hue': '70deg', ease: 'none' }, 0);

      /* ---- ABOUT PARALLAX ----
         Every image and text block below drifts continuously at its
         own rate the whole time it's passing through the viewport —
         not a one-shot reveal, an actual multi-layer parallax. Each
         call is its own ScrollTrigger scoped to that one element
         (trigger:el, top-bottom to bottom-top of THAT element, not
         the section), so neighboring elements move independently of
         each other rather than in lockstep. yPercent is relative to
         the element's own size, and range is how far it drifts either
         side of its natural position (so an element with a bigger
         range visibly moves faster than one with a smaller range as
         you scroll past both) — that's what makes it read as
         layered/parallaxed rather than everything just nudging
         together. Images live inside .about-parallax-media
         (about-hero.liquid), which is what keeps an oversized,
         absolutely-positioned <img> from exposing empty space at its
         edges while it drifts. */
      function parallax(el, range) {
        if (!el) return;
        gsap.fromTo(el, { yPercent: -range }, {
          yPercent: range, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
        });
      }

      parallax($('.about-story__media img'), 10);
      parallax($('.about-story__lead'), 5);
      parallax($('.about-story__body'), 14);

      $$('.about-values__row').forEach(row => {
        parallax($('.about-values__num', row), 4);
        parallax($('h3', row), 8);
        parallax($('p', row), 16);
      });

      $$('.about-craft__row').forEach(row => {
        parallax($('.about-craft__media img', row), 10);
        parallax($('h3', row), 6);
        parallax($('p', row), 16);
      });
    }

    ScrollTrigger.refresh();
  }

  /* ───────────────────────── 12. VELOCITY MARQUEES ───────────────────────── */
  const MQ_HTML = new WeakMap();
  function initMarquee(el) {
    const track = $('.marquee__track', el);
    if (!MQ_HTML.has(el)) MQ_HTML.set(el, track.innerHTML);
    else track.innerHTML = MQ_HTML.get(el);
    const set = $('.marquee__set', track);
    const setW = set.getBoundingClientRect().width;
    if (!setW) return null;
    const copies = Math.ceil((window.innerWidth * 2) / setW) + 1;
    for (let i = 1; i < copies; i++) track.appendChild(set.cloneNode(true));
    return { track, setW, x: 0, base: parseFloat(el.dataset.speed) || 40 };
  }

  const marquees = [];
  function bootMarquees() {
    marquees.length = 0;
    $$('[data-marquee]').forEach(el => {
      const m = initMarquee(el);
      if (m) marquees.push(m);
    });
  }

  let dir = 1, vel = 0;
  if (lenis) lenis.on('scroll', e => {
    const v = e.velocity ?? 0;
    if (Math.abs(v) > 0.2) dir = v > 0 ? 1 : -1;
    vel = v;
  });

  let last = performance.now();
  function marqueeLoop(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    vel *= 0.92;
    const boost = Math.min(1200, Math.abs(vel) * 22);
    marquees.forEach(m => {
      m.x -= (m.base + boost) * dt * dir;
      m.x = ((m.x % m.setW) + m.setW) % m.setW - m.setW;
      m.track.style.transform = 'translate3d(' + m.x + 'px,0,0) skewX(' + Math.max(-6, Math.min(6, -vel * 0.14)) + 'deg)';
    });
    requestAnimationFrame(marqueeLoop);
  }

  /* ───────────────────────── 13. MAGNETIC BUTTON ───────────────────────── */
  if (hasGSAP && window.matchMedia('(hover:hover)').matches) {
    $$('.magnet').forEach(el => {
      const xT = gsap.quickTo(el, 'x', { duration: .5, ease: 'power3' });
      const yT = gsap.quickTo(el, 'y', { duration: .5, ease: 'power3' });
      el.addEventListener('mousemove', e => {
        const r = el.getBoundingClientRect();
        xT((e.clientX - r.left - r.width / 2) * .38);
        yT((e.clientY - r.top - r.height / 2) * .5);
      });
      el.addEventListener('mouseleave', () => { xT(0); yT(0); });
    });
  }

  /* ───────────────────────── 14. COLLECTION FILTERS ─────────────────────────
     Real Shopify filtering (collection.filters) rendered as a GET
     form — works with no JS at all via native submission (Enter key,
     or a screen reader driving the checkboxes). This layer is purely
     an enhancement: auto-submit on change so picking a filter feels
     instant, plus the dropdown open/close/outside-click behavior for
     the "toolbar" look instead of a plain stacked filter list. */
  (function () {
    const form = $('#collFilters');
    if (!form) return;

    $$('input[type="checkbox"], input[type="number"], select[name="sort_by"]', form).forEach(el => {
      el.addEventListener('change', () => form.requestSubmit());
    });

    const groups = $$('.filter-group', form);
    function closeAll(except) {
      groups.forEach(g => {
        if (g === except) return;
        g.classList.remove('is-open');
        $('.filter-group__toggle', g).setAttribute('aria-expanded', 'false');
      });
    }
    groups.forEach(g => {
      const btn = $('.filter-group__toggle', g);
      btn.addEventListener('click', e => {
        e.preventDefault();
        const willOpen = !g.classList.contains('is-open');
        closeAll();
        g.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', String(willOpen));
      });
    });
    document.addEventListener('click', e => {
      if (!e.target.closest('.filter-group')) closeAll();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeAll();
    });

    /* Category — not a real Shopify filter (this shop's Search &
       Discovery config doesn't expose product type as a facet), so
       it's a plain client-side show/hide by product type instead of
       a form field: reads each card's already-rendered .card__sub
       text (the product type shown under the name) rather than
       requiring a data attribute added to the shared card markup. */
    const catGroup = $('[data-category-group]');
    if (catGroup) {
      const catOptions = $$('[data-category-option]', catGroup);
      const countEl = $('.sec-head .meta .mono');
      const cards = $$('[data-product-card]');
      catOptions.forEach(opt => {
        opt.addEventListener('click', () => {
          const type = opt.dataset.type;
          catOptions.forEach(o => o.classList.toggle('is-active', o === opt));
          let visible = 0;
          cards.forEach(card => {
            const subEl = $('.card__sub', card);
            const cardType = subEl ? subEl.textContent.trim() : '';
            const show = !type || cardType === type;
            card.style.display = show ? '' : 'none';
            if (show) visible++;
          });
          if (countEl) countEl.textContent = visible + (visible === 1 ? ' piece' : ' pieces');
          closeAll();
        });
      });
    }
  })();

  /* ───────────────────────── 15. BOOT ───────────────────────── */
  function intro() {
    document.documentElement.classList.add('loaded');
    if (!hasGSAP || reduced) return;

    /* hero/header reveal, no pre-roll loading screen */
    gsap.timeline()
      .fromTo('.hero__title .ln > span', { yPercent: 112 },
        { yPercent: 0, duration: 1.15, stagger: .08, ease: 'expo.out' }, 0)
      .fromTo('.hero__kicker, .hero__foot', { y: 26, opacity: 0 },
        { y: 0, opacity: 1, duration: .9, stagger: .09, ease: 'power3.out' }, .2)
      .fromTo('.hdr, .scroll-cue', { y: -14, opacity: 0 },
        { y: 0, opacity: 1, duration: .8, ease: 'power3.out' }, .15)
      .set('.hdr', { clearProps: 'transform' });
  }

  function boot() {
    bootMarquees();
    buildScenes();
    requestAnimationFrame(marqueeLoop);
    intro();
    setTimeout(() => { if (hasGSAP) ScrollTrigger.refresh(); }, 800);
  }

  window.addEventListener('load', () => {
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(boot).catch(boot);
    else boot();
  });

  let rz, lastW = window.innerWidth;
  window.addEventListener('resize', () => {
    clearTimeout(rz);
    rz = setTimeout(() => {
      if (window.innerWidth !== lastW) { lastW = window.innerWidth; bootMarquees(); }
      if (hasGSAP) ScrollTrigger.refresh();
    }, 250);
  });
})();
